import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormHelperText,
  Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import socialService, { ProfileAliasDto, CreateAliasRequestDto, UpdateAliasRequestDto } from '../../services/socialService';
import registrationService from '../../services/registrationService';
import accountService from '../../services/accountService';
import { useAuth } from '../../hooks/useAuth';
import AccountClosureDialog from './AccountClosureDialog';

interface AliasManagerProps {
  aliases: ProfileAliasDto[];
  onRefresh: () => void;
  /** When provided (e.g. when shown in a drawer), show a Close/Done button that calls this. */
  onClose?: () => void;
}

const ALIAS_USERNAME_REGEX = /^[a-zA-Z0-9_-]+$/;

// Validates that alias has at least 3 letters and 2 numbers
const validateLettersAndNumbers = (name: string): { valid: boolean; reason?: string } => {
  const letterCount = (name.match(/[a-zA-Z]/g) || []).length;
  const digitCount = (name.match(/[0-9]/g) || []).length;
  if (letterCount < 3 || digitCount < 2) {
    return { valid: false, reason: 'Must contain at least 3 letters and 2 numbers.' };
  }
  return { valid: true };
};

const INSTRUCTION_BOX = `All usernames must be unique and must contain at least 3 letters and 2 numbers. We recommend one that reflects your true name, like "John_Smith-1040" which is marked as public, and one that is non-identifying, like "BlueJelly99" marked as "Semi-Anonymous." It is semi-anonymous because your true identity is not shared with vendors or advertisers, but it is traceable in the event of fraud or court orders, because it is linked to your main account. You have one password which works with all your aliases. You are allowed only three free aliases at this time. Credentialed business accounts are unlimited. Note: usernames are not case-sensitive. John is the same as joHn.`;

const AliasManager: React.FC<AliasManagerProps> = ({ aliases, onRefresh, onClose }) => {
  // Only count free aliases (types 1, 2, 3) toward the 3-alias limit
  // Type 4 = Credentialed (Business-Vendor, Business-Marketer, Business-Developer) - unlimited
  // Types 1-3 are personal aliases that count toward the free alias limit.
  // Type 4 is a credentialed business alias (unlimited).
  // Type 5 is a minor's account mirror (not a personal alias, does not count toward limit).
  const freeAliases = aliases.filter(a => a.intAliasType >= 1 && a.intAliasType <= 3);
  const freeAliasCount = freeAliases.length;
  const { user, handleLogout } = useAuth();
  const baseUsername = (user?.username ?? '').trim().toLowerCase();
  const [openDialog, setOpenDialog] = useState(false);
  const [newAlias, setNewAlias] = useState<CreateAliasRequestDto>({
    chrAliasName: '',
    chrDisplayName: '',
    intAliasType: 1
  });
  const [usernameAvailability, setUsernameAvailability] = useState<{ checking: boolean; available?: boolean; reason?: string }>({ checking: false });
  const [aliasNameTouched, setAliasNameTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editAlias, setEditAlias] = useState<ProfileAliasDto | null>(null);
  const [editForm, setEditForm] = useState<{ chrDisplayName: string; intAliasType: number }>({ chrDisplayName: '', intAliasType: 1 });
  const [editSaving, setEditSaving] = useState(false);

  // Account closure state
  const [closureDialogOpen, setClosureDialogOpen] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(0);

  const checkUsername = useCallback(async (name: string) => {
    const trimmed = (name || '').trim();
    if (trimmed.length < 3) {
      setUsernameAvailability({ checking: false, available: false, reason: 'At least 3 characters required.' });
      return;
    }
    if (!ALIAS_USERNAME_REGEX.test(trimmed)) {
      setUsernameAvailability({ checking: false, available: false, reason: 'Only letters, numbers, underscore (_), and hyphen (-) are allowed.' });
      return;
    }
    // Validate 3 letters + 2 numbers requirement
    const lettersNumbersCheck = validateLettersAndNumbers(trimmed);
    if (!lettersNumbersCheck.valid) {
      setUsernameAvailability({ checking: false, available: false, reason: lettersNumbersCheck.reason });
      return;
    }
    setUsernameAvailability({ checking: true });
    try {
      const result = await registrationService.checkUsername(trimmed);
      setUsernameAvailability({ checking: false, available: result.available, reason: result.reason });
    } catch {
      setUsernameAvailability({ checking: false, available: undefined, reason: 'Could not check availability.' });
    }
  }, []);

  const handleCreateAlias = async () => {
    const aliasName = (newAlias.chrAliasName || '').trim();
    if (aliasName.length < 3) {
      alert('Alias name must be at least 3 characters.');
      return;
    }
    if (!ALIAS_USERNAME_REGEX.test(aliasName)) {
      alert('Alias name can only contain letters, numbers, underscores (_), and hyphens (-).');
      return;
    }
    // Validate 3 letters + 2 numbers requirement
    const lettersNumbersCheck = validateLettersAndNumbers(aliasName);
    if (!lettersNumbersCheck.valid) {
      alert(lettersNumbersCheck.reason || 'Alias name must contain at least 3 letters and 2 numbers.');
      return;
    }
    if (freeAliasCount >= 3) {
      alert('You are allowed only three free aliases at this time. Credentialed business accounts are unlimited.');
      return;
    }
    setIsSubmitting(true);
    try {
      const avail = await registrationService.checkUsername(aliasName);
      if (!avail.available) {
        alert(avail.reason || 'This username is already taken.');
        return;
      }
      await socialService.createAlias(newAlias);
      setOpenDialog(false);
      setNewAlias({ chrAliasName: '', chrDisplayName: '', intAliasType: 1 });
      setUsernameAvailability({ checking: false });
      setAliasNameTouched(false);
      onRefresh();
    } catch (error: any) {
      alert(error.message || 'Failed to create alias');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetPrimary = async (aliasId: string) => {
    try {
      await socialService.setPrimaryAlias(aliasId);
      onRefresh();
    } catch (error) {
      alert('Failed to set primary alias');
    }
  };

  const handleToggleTrueNameLock = async (alias: ProfileAliasDto) => {
    const aliasId = (alias as any).idAliasID ?? (alias as any).IdAliasID;
    if (!aliasId) {
      alert('Invalid alias');
      return;
    }

    if (!alias.bitIsPrimary) {
      alert('Set this alias as primary before locking true name to it.');
      return;
    }

    const nextLockState = !Boolean(alias.bitTrueNameLocked);
    if (nextLockState) {
      const confirmed = confirm(
        'Lock true name to this primary alias?\n\n' +
        'Its display name will be forced to your Account Settings true name and cannot be edited until unlocked.'
      );
      if (!confirmed) return;
    }

    try {
      await socialService.setTrueNameLock(String(aliasId), nextLockState);
      onRefresh();
    } catch (error: any) {
      alert(error.message || 'Failed to update true-name lock');
    }
  };

  const handleDeleteAlias = async (alias: ProfileAliasDto) => {
    const aliasId = (alias as any).idAliasID ?? (alias as any).IdAliasID;

    // If this is the primary alias and there are others → user must reassign primary first
    if (alias.bitIsPrimary && aliases.length > 1) {
      alert(
        'Your primary alias cannot be deleted while other aliases exist.\n\n' +
        'To remove this alias, first set another alias as primary (click the star icon), ' +
        'then delete this one.\n\n' +
        'Alternatively, to close your entire account, remove all other aliases first so ' +
        'this is the only one remaining, then click its delete button.'
      );
      return;
    }

    // If this is the last remaining alias → offer full account closure
    if (alias.bitIsPrimary && aliases.length === 1) {
      // Fetch balance for the warning dialog
      try {
        const bal = await accountService.getBalance();
        setCurrentBalance(bal.availableBalance ?? 0);
      } catch {
        setCurrentBalance(0);
      }
      setClosureDialogOpen(true);
      return;
    }

    // Normal non-primary alias delete
    if (!confirm(`Are you sure you want to delete the alias "${alias.chrAliasName}"?`)) return;
    try {
      await socialService.deleteAlias(aliasId);
      onRefresh();
    } catch (error: any) {
      alert(error.message || 'Failed to delete alias');
    }
  };

  const handleOpenEdit = (alias: ProfileAliasDto) => {
    setEditAlias(alias);
    setEditForm({
      chrDisplayName: alias.chrDisplayName?.trim() || alias.chrAliasName || '',
      intAliasType: Number(alias.intAliasType) >= 1 && Number(alias.intAliasType) <= 5 ? alias.intAliasType : 2,
    });
  };

  const handleSaveEdit = async () => {
    if (!editAlias) return;
    const aliasId = (editAlias as any).idAliasID ?? (editAlias as any).IdAliasID;
    if (!aliasId) {
      alert('Invalid alias');
      return;
    }
    const nextDisplayName = editForm.chrDisplayName.trim() || editAlias.chrAliasName || '';
    const currentDisplayName = (editAlias.chrDisplayName || '').trim();
    const isChangingLockedVerifiedName =
      Boolean(editAlias.bitTrueNameLocked) &&
      Boolean(editAlias.bitIdVerified) &&
      nextDisplayName.length > 0 &&
      nextDisplayName !== currentDisplayName;
    if (isChangingLockedVerifiedName) {
      const confirmed = window.confirm(
        'Changing this display name will degrade verification status from "ID verified Name" to "Traceable". Continue?'
      );
      if (!confirmed) {
        return;
      }
    }
    setEditSaving(true);
    try {
      const request: UpdateAliasRequestDto = {
        chrDisplayName: nextDisplayName,
        intAliasType: editForm.intAliasType
      };
      await socialService.updateAlias(String(aliasId), request);
      setEditAlias(null);
      onRefresh();
    } catch (error: any) {
      alert(error.message || 'Failed to update alias');
    } finally {
      setEditSaving(false);
    }
  };

  const getAliasTypeColor = (type: number) => {
    switch (type) {
      case 1: return 'primary';
      case 2: return 'secondary';
      case 3: return 'default';
      case 5: return 'info';   // Minor's Account
      default: return 'default';
    }
  };

  const getDisplayAliasType = (alias: ProfileAliasDto): { label: string; type: number } => {
    const normalizedType = Number(alias.intAliasType) >= 1 && Number(alias.intAliasType) <= 5
      ? Number(alias.intAliasType)
      : 2;
    const rawLabel = (alias.aliasTypeName || '').trim();
    const label = !rawLabel || rawLabel.toLowerCase() === 'unknown' ? 'Private' : rawLabel;
    return { label, type: normalizedType };
  };

  const trimmedAliasName = (newAlias.chrAliasName || '').trim();
  const aliasNameValid = trimmedAliasName.length >= 3 && 
    ALIAS_USERNAME_REGEX.test(trimmedAliasName) && 
    validateLettersAndNumbers(trimmedAliasName).valid;
  const canCreate = aliasNameValid && freeAliasCount < 3;

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 1,
            bgcolor: 'info.light',
            color: 'info.contrastText'
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {INSTRUCTION_BOX}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5">
            Manage Aliases
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {onClose && (
              <Button variant="outlined" onClick={onClose}>
                Done
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setOpenDialog(true);
                setUsernameAvailability({ checking: false });
                setAliasNameTouched(false);
              }}
              disabled={freeAliasCount >= 3}
            >
              Create Alias
            </Button>
          </Box>
        </Box>

        <List>
          {aliases.map((alias) => {
            const displayAliasType = getDisplayAliasType(alias);
            // Login alias = the one you sign in with. Always show actual login username from auth, not DB.
            const isLoginAlias = baseUsername && (alias.chrAliasName || '').trim().toLowerCase() === baseUsername;
            const displayName = (alias.chrDisplayName || alias.chrAliasName || '').trim();
            const handle = isLoginAlias ? (user?.username ?? (alias.chrBaseUserName || alias.chrAliasName || '')).trim() : alias.chrAliasName;
            const isOnlyAlias = aliases.length === 1;

            return (
              <React.Fragment key={alias.idAliasID}>
                {alias.bitIsPrimary && (
                  <Box sx={{ px: 2, pb: 1 }}>
                    <Alert
                      severity={isOnlyAlias ? 'warning' : 'info'}
                      icon={<InfoOutlinedIcon />}
                      sx={{ fontSize: '0.82rem' }}
                    >
                      {isOnlyAlias ? (
                        <>
                          <strong>This is your only alias and the account owner.</strong> Deleting it will
                          permanently close your entire Summa account, abandon any remaining balance, and
                          release this username for others to register.
                        </>
                      ) : (
                        <>
                          <strong>Primary alias — account owner.</strong> This alias cannot be deleted while
                          other aliases exist. To remove it, first set another alias as primary (click the
                          star icon on that alias), then delete this one.
                        </>
                      )}
                    </Alert>
                  </Box>
                )}
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {displayName}
                        <Chip
                          label={displayAliasType.label}
                          size="small"
                          color={getAliasTypeColor(displayAliasType.type) as any}
                        />
                        {alias.bitTrueNameLocked && (
                          <Chip label="True Name Locked" size="small" color="secondary" variant="outlined" />
                        )}
                        {alias.bitTrueNameLocked && alias.bitIdVerified && (
                          <Chip label="ID Verified Name" size="small" color="secondary" />
                        )}
                        {alias.bitIsPrimary && <StarIcon color="primary" />}
                      </Box>
                    }
                    secondary={`@${handle}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton onClick={() => handleOpenEdit(alias)} title="Edit display name and profile status">
                      <EditIcon />
                    </IconButton>
                    {alias.bitIsPrimary && (
                      <IconButton
                        onClick={() => handleToggleTrueNameLock(alias)}
                        title={alias.bitTrueNameLocked ? 'Unlock true name from this alias' : 'Lock true name to this alias'}
                        color={alias.bitTrueNameLocked ? 'secondary' : 'default'}
                      >
                        {alias.bitTrueNameLocked ? <LockOpenIcon /> : <LockIcon />}
                      </IconButton>
                    )}
                    {!alias.bitIsPrimary && (
                      <IconButton onClick={() => handleSetPrimary(alias.idAliasID)} title="Set as primary">
                        <StarBorderIcon />
                      </IconButton>
                    )}
                    <IconButton
                      onClick={() => handleDeleteAlias(alias)}
                      title={alias.bitIsPrimary && aliases.length > 1
                        ? 'Cannot delete primary while other aliases exist'
                        : alias.bitIsPrimary && isOnlyAlias
                          ? 'Close account (last alias)'
                          : 'Delete alias'}
                      color={alias.bitIsPrimary && isOnlyAlias ? 'error' : 'default'}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            );
          })}
        </List>

        {/* Create Alias Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Create New Alias</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <TextField
                label="Alias Name"
                value={newAlias.chrAliasName}
                onChange={(e) => {
                  const aliasName = e.target.value;
                  setNewAlias((prev) => ({
                    ...prev,
                    chrAliasName: aliasName,
                    chrDisplayName: prev.chrDisplayName.trim() ? prev.chrDisplayName : aliasName,
                  }));
                  setUsernameAvailability({ checking: false });
                }}
                onBlur={() => {
                  setAliasNameTouched(true);
                  checkUsername(newAlias.chrAliasName);
                }}
                error={aliasNameTouched && usernameAvailability.available === false}
                helperText={
                  aliasNameTouched && usernameAvailability.checking
                    ? 'Checking...'
                    : aliasNameTouched && usernameAvailability.available === false
                      ? usernameAvailability.reason
                      : 'Must have at least 3 letters and 2 numbers. Allowed: letters, numbers, underscore (_), hyphen (-). Not case-sensitive.'
                }
                placeholder="e.g. John_Smith-1040 or BlueJelly99"
              />
              <TextField
                label="Display Name"
                value={newAlias.chrDisplayName}
                onChange={(e) => setNewAlias({ ...newAlias, chrDisplayName: e.target.value })}
                helperText="Shown as the 'From' name in emails and on your profile. Defaults to the alias name above if left blank."
                placeholder="Defaults to alias name"
              />
              <FormControl fullWidth>
                <InputLabel>Alias Type</InputLabel>
                <Select
                  value={newAlias.intAliasType}
                  onChange={(e) => setNewAlias({ ...newAlias, intAliasType: Number(e.target.value) })}
                  label="Alias Type"
                >
                  <MenuItem value={1}>Public</MenuItem>
                  <MenuItem value={2}>Private</MenuItem>
                  <MenuItem value={3}>Semi-Anonymous</MenuItem>
                </Select>
                <FormHelperText>
                  {newAlias.intAliasType === 1 && 'Profile can be public; your name can be linked to this username.'}
                  {newAlias.intAliasType === 2 && 'No public profile; only you see this alias. Good for minimal footprint.'}
                  {newAlias.intAliasType === 3 && 'Profile can be public (e.g. for gaming or interests) but your real identity is not shared with vendors; traceable to your account for fraud/legal.'}
                </FormHelperText>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateAlias} variant="contained" disabled={!canCreate || isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Alias Dialog - change display name and profile status (public/private/semi-anonymous) */}
        <Dialog open={!!editAlias} onClose={() => setEditAlias(null)}>
          <DialogTitle>Edit alias</DialogTitle>
          <DialogContent>
            {editAlias && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2, minWidth: 320 }}>
                <Typography variant="body2" color="text.secondary">
                  @{editAlias.chrAliasName} (username cannot be changed)
                </Typography>
                <TextField
                  label="Display Name"
                  value={editForm.chrDisplayName}
                  onChange={(e) => setEditForm({ ...editForm, chrDisplayName: e.target.value })}
                  helperText={editAlias.bitTrueNameLocked
                    ? 'This alias is currently ID Verified Name locked. Changing it will downgrade status to Traceable.'
                    : 'Shown in emails and on your profile'}
                  fullWidth
                />
                <FormControl fullWidth>
                  <InputLabel>Profile status</InputLabel>
                  <Select
                    value={editForm.intAliasType}
                    onChange={(e) => setEditForm({ ...editForm, intAliasType: Number(e.target.value) })}
                    label="Profile status"
                  >
                    <MenuItem value={1}>Public</MenuItem>
                    <MenuItem value={2}>Private</MenuItem>
                    <MenuItem value={3}>Semi-Anonymous</MenuItem>
                  </Select>
                  <FormHelperText>
                    {editForm.intAliasType === 1 && 'Profile can be public; your name can be linked to this username.'}
                    {editForm.intAliasType === 2 && 'No public profile; only you see this alias.'}
                    {editForm.intAliasType === 3 && 'Profile can be public but your real identity is not shared with vendors.'}
                  </FormHelperText>
                </FormControl>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditAlias(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} variant="contained" disabled={editSaving}>
              {editSaving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>

      {/* Account Closure Dialog — triggered when user tries to delete their only/primary alias */}
      <AccountClosureDialog
        open={closureDialogOpen}
        onClose={() => setClosureDialogOpen(false)}
        onClosureComplete={async () => {
          setClosureDialogOpen(false);
          await handleLogout();
        }}
        currentBalance={currentBalance}
      />
    </Card>
  );
};

export default AliasManager;

